import { Flex, HStack, Separator, VStack } from "@chakra-ui/react"
import { useState } from "react"
import ConfigureRange, { ConfigureRangeSkeleton } from "@hydrologiq/shared/reporting/ConfigureRange"
import { Granularity } from "@hydrologiq/common/types/reporting/Granularity"
import { DateTime, days } from "timezonecomplete"
import Informative from "@hydrologiq/shared/Informative"
import CustomerReportingUsage, {
  CustomerReportingUsageSkeleton,
  CustomerReportingUsageView,
} from "@hydrologiq/customers/components/reporting/CustomerReportingUsage"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { FaChartColumn, FaTableList } from "react-icons/fa6"

function ReportingUsage({
  customer,
  loading: customerLoading,
  error: customerError,
  min,
  max,
}: {
  customer?: gqlTypes.Customer | undefined
  loading: boolean
  error: string | undefined
  min: DateTime
  max: DateTime
}) {
  const customerName = customer?.name

  const [start, setStart] = useState<DateTime>(min)
  const [end, setEnd] = useState<DateTime>(min.add(days(9)))

  const [granularity, setgranularity] = useState<Granularity>(Granularity.Daily)
  const [view, setView] = useState<CustomerReportingUsageView>("table")

  const loading = customerLoading
  const error = customerError

  return error || (!loading && !customer) ? (
    <Informative
      heading="Waiting for data to be available"
      text="Reporting information will be available when an asset has been utilised."
    />
  ) : (
    <>
      <VStack align="stretch">
        <HStack
          gap={5}
          height="100%"
          justify="flex-start"
          align="flex-end"
          flexDirection={{ base: "column", lg: "row" }}
        >
          <VStack align="stretch" width={{ base: "100%", lg: "50%" }}>
            {loading ? (
              <ConfigureRangeSkeleton />
            ) : (
              <ConfigureRange
                min={min}
                max={max}
                end={end}
                granularityDefault={Granularity.Daily}
                callback={(granularity, start, end) => {
                  setgranularity(granularity)
                  setStart(start)
                  setEnd(end)
                }}
              />
            )}
          </VStack>
          <Flex height="100%">
            <SegmentedControl
              variant="hydrologiq_icon"
              items={[
                { value: "table", label: "Table", icon: FaTableList },
                { value: "chart", label: "Chart", icon: FaChartColumn },
              ]}
              onValueChange={(value) => setView(value.value as "chart" | "table")}
              value={view}
            />
          </Flex>
        </HStack>

        {loading || !customer || !customerName || !(end && start && end.greaterThan(start)) ? (
          <CustomerReportingUsageSkeleton view={view} />
        ) : (
          <CustomerReportingUsage
            customerId={customer.id}
            end={end}
            start={start}
            granularity={granularity}
            customerName={customerName}
            view={view}
            source={(customer as any).source as "timescale" | "influxdb" | undefined}
          />
        )}
      </VStack>
    </>
  )
}

export default ReportingUsage